import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { IncidentViewType } from '@/types';

import { INCIDENT_DETAILS, INCIDENT_FILTER, INCIDENTS_LIST } from '@/utils/constants';
import {
  SEARCH_PARAM_DRAWER,
  SEARCH_PARAM_INCIDENT_ID,
  SEARCH_PARAM_INCIDENTS,
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_LOCK_PIN_TYPE,
  SEARCH_PARAM_SECTION_ID,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_VIEW,
  VIEW_LADDER,
} from '@/utils/constants/queries';

const useIncidentState = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showFilter, setShowFilter] = useState(false);

  const isShowSidePanel = searchParams?.get(SEARCH_PARAM_SHOW) === SEARCH_PARAM_INCIDENTS;
  const isShowIncidentDetails = !!searchParams.get(SEARCH_PARAM_INCIDENT_ID);

  const paramLockedStationId = searchParams.get(SEARCH_PARAM_LOCK_PIN) ?? '';
  const isLadderView = searchParams.get(SEARCH_PARAM_VIEW) === VIEW_LADDER;
  const paramSectionId = searchParams.get(SEARCH_PARAM_SECTION_ID);
  const lockType = searchParams?.get(SEARCH_PARAM_LOCK_PIN_TYPE);

  const handleToggleSidePanel = useCallback(() => {
    const updatedParams = new URLSearchParams();

    if (isLadderView) {
      updatedParams.set(SEARCH_PARAM_VIEW, VIEW_LADDER);

      if (paramSectionId) {
        updatedParams.set(SEARCH_PARAM_SECTION_ID, paramSectionId);
      }
    }

    if (paramLockedStationId) {
      updatedParams.set(SEARCH_PARAM_LOCK_PIN, paramLockedStationId);
      updatedParams.set(SEARCH_PARAM_DRAWER, 'true');
    }

    if (lockType) {
      updatedParams.set(SEARCH_PARAM_LOCK_PIN_TYPE, lockType);
    }

    setSearchParams(updatedParams);
    setShowFilter(false);
  }, [isShowSidePanel, isLadderView, paramLockedStationId, paramSectionId, lockType]);

  const handleBackNavigation = useCallback(() => {
    if (showFilter) {
      setShowFilter(false);

      return;
    }

    if (isShowIncidentDetails) {
      setSearchParams((prev) => {
        prev.delete(SEARCH_PARAM_INCIDENT_ID);

        return prev;
      });
    }
  }, [showFilter, isShowIncidentDetails]);

  const handleShowFilter = useCallback(() => setShowFilter(true), []);
  const handleHideFilter = useCallback(() => setShowFilter(false), []);

  let viewType: IncidentViewType = INCIDENTS_LIST;

  if (showFilter) {
    viewType = INCIDENT_FILTER;
  } else if (isShowIncidentDetails) {
    viewType = INCIDENT_DETAILS;
  }

  const headerText = useMemo(() => {
    if (viewType === INCIDENT_FILTER) {
      return t('header.incident_filter');
    }

    if (viewType === INCIDENT_DETAILS) {
      return t('header.incident_details');
    }

    return t('header.incidents');
  }, [viewType, t]);

  return {
    viewType,
    isShowSidePanel,
    headerText,
    hasHeaderBottomBorder: isShowIncidentDetails || showFilter,
    hasBackIcon: isShowIncidentDetails || showFilter,
    showFilter,
    handleToggleSidePanel,
    handleBackNavigation,
    handleShowFilter,
    handleHideFilter,
  };
};

export default useIncidentState;
